import React from 'react';
import Row from '@components/Table/rows/Row';
import {useTableContext} from '@components/Table';
import {ClaimTableColumn} from './types';
import IconButtonCell from '@components/Table/cells/IconButtonCell';
import {TrashIcon} from '@tempo/icons';
import ClaimDetailCell from './ClaimDetailCell';
import type {Claim} from '@deezer/next/services/api/Starmania/requests/claim/types';
import {ClaimStatus} from '@deezer/next/services/api/Starmania/requests/claim/types';
import DateCell from '@components/Table/cells/DateCell';
import StatusCell from '@deezer/next/features/ClaimAccess/components/ClaimTable/StatusCell';
import {useDisclosure} from '@tempo/core';
import CollapsibleRow from '@components/Table/rows/CollapsibleRow';
import ManagerClaimCollapsedContent from './ManagerClaimCollapsedContent';
import {useTranslation} from 'next-i18next';
import Td from '@components/Table/core/Td';

type ClaimTableRowProps = {
	claim: Claim;
	rowIndex: number;
	onRemove: (claim: Claim) => void;
};

const ClaimTableRow = ({claim, rowIndex, onRemove}: ClaimTableRowProps) => {
	const {t} = useTranslation('d4c');
	const {columns} = useTableContext<ClaimTableColumn>();
	const {isOpen, onToggle} = useDisclosure();

	const handleRemoveClick = () => {
		onRemove(claim);
	};

	const columnsContent = columns.map((column, index) => {
		const key = column.id + index;
		switch (column.id) {
			case ClaimTableColumn.DETAILS:
				return (
					<ClaimDetailCell
						key={key}
						claim={claim}
						display={column.display}
						onToggle={onToggle}
						isCollapsed={!isOpen}
					/>
				);
			case ClaimTableColumn.DATE:
				return (
					<DateCell
						key={key}
						date={new Date(claim.createdAt)}
						display={column.display}
					/>
				);
			case ClaimTableColumn.STATUS:
				return <StatusCell status={claim.status} display={column.display} />;
			case ClaimTableColumn.DELETE:
				if (
					claim.status === ClaimStatus.DENIED ||
					claim.status === ClaimStatus.DELETED ||
					claim.status === ClaimStatus.APPROVED
				) {
					return <Td key={key} />;
				}
				return (
					<IconButtonCell
						key={key}
						aria-label={'delete'}
						icon={<TrashIcon />}
						display={column.display}
						onClick={handleRemoveClick}
						tooltip={
							claim.status !== ClaimStatus.PROCESSING
								? t('generic_action_removerequest_d4c-web')
								: undefined
						}
						iconButtonProps={{
							isDisabled: claim.status === ClaimStatus.PROCESSING,
						}}
					/>
				);
		}
	});

	if (claim.stakeholderType === 'Manager' && claim.artists.length > 1) {
		return (
			<CollapsibleRow
				index={rowIndex}
				isCollapsed={!isOpen}
				onToggle={onToggle}
				collapsedContent={<ManagerClaimCollapsedContent claim={claim} />}
			>
				{columnsContent}
			</CollapsibleRow>
		);
	}

	return <Row index={rowIndex}>{columnsContent}</Row>;
};

export default ClaimTableRow;
